import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import Cookies from "js-cookie";
import {
  Crown, WhatsappLogo, Download, ArrowRight, Eye, ImageSquare,
  CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * FeaturedWeeklyCard — "Spingi questa casa stasera".
 *
 * Mostra l'annuncio della settimana scelto automaticamente dal backend
 * (più visualizzazioni in 7gg, fallback al più recente con foto). Due CTA:
 *   1. Scarica immagine 1080×1080 — pronta da inviare su WhatsApp
 *   2. Apri WhatsApp — wa.me con messaggio pre-compilato
 *
 * Si nasconde se l'agente non ha annunci con foto (silenzioso).
 *
 * iter v32 r37p — User M1446: «se uno ne ha di più cosa succede?». Adesso
 * la card ha prev/next + "1 di N" per ciclare tra i primi 8 annunci attivi
 * dell'agente. Riduzione padding/sizes per occupare meno spazio rispetto
 * alla Vetrina (che adesso ha priorità in cima).
 *
 * Endpoint: GET /api/featured/weekly?index=N
 */
export default function FeaturedWeeklyCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [index, setIndex] = useState(0);

  const fetchAt = useCallback(async (n) => {
    try {
      const { data: d } = await api.get(`/featured/weekly?index=${n}`);
      setData(d);
    } catch {
      setData({ available: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/featured/weekly?index=0");
        if (alive) setData(d);
      } catch {
        if (alive) setData({ available: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const total = data?.total_active || 0;
  const goPrev = useCallback(() => {
    if (total <= 1) return;
    const next = (index - 1 + total) % total;
    setIndex(next);
    fetchAt(next);
  }, [index, total, fetchAt]);
  const goNext = useCallback(() => {
    if (total <= 1) return;
    const next = (index + 1) % total;
    setIndex(next);
    fetchAt(next);
  }, [index, total, fetchAt]);

  const coverUrl = useMemo(() => {
    if (!data?.cover_thumb_path) return null;
    const token = Cookies.get("vista_jwt");
    return `${BACKEND_URL}/api/files?path=${encodeURIComponent(data.cover_thumb_path)}${token ? `&auth=${token}` : ""}`;
  }, [data]);

  if (loading || !data?.available) return null;

  // iter v32 r49 — User M1448: «fai vedere un'immagine in anteprima».
  // Il link DEVE puntare a `/api/share/{public_id}` (smart URL): i bot
  // WhatsApp/iMessage/Telegram ricevono HTML con meta OG (foto, titolo,
  // agenzia) e l'anteprima si vede; gli umani vengono 302-redirected a
  // /listing/{public_id} (la SPA). Prima usavamo `/p/{public_id}` che
  // però era la rotta dei LegacySlug agente — niente preview, e 404 per
  // i public_id listing che non corrispondono a uno slug.
  const publicLink = data.public_id ? `${BACKEND_URL}/api/share/${data.public_id}` : null;
  const waText = publicLink
    ? `${data.whatsapp_message}\n\n${publicLink}`
    : data.whatsapp_message;
  const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;

  const downloadImage = async () => {
    setDownloading(true);
    try {
      const res = await api.get(data.social_image_url.replace(/^\/api/, ""), {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pronto-annuncio-settimana-${(data.title || "casa").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Immagine 1080×1080 scaricata. Allegala in chat prima del messaggio.");
    } catch (e) {
      toast.error("Non riesco a generare l'immagine ora. Riprova tra un attimo.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      data-testid="featured-weekly-card"
      className="relative overflow-hidden rounded-2xl border border-[#1C1C1A] bg-[#1C1C1A] text-white mb-6 shadow-[0_8px_28px_-14px_rgba(28,28,26,0.4)]"
    >
      {/* Subtle gold gradient on top corner */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-[#C8A66B]/30 to-transparent blur-2xl pointer-events-none"
      />

      <div className="relative p-3.5 sm:p-4 grid grid-cols-[88px_1fr] sm:grid-cols-[96px_1fr] gap-3.5 sm:gap-4 items-center">
        {/* Cover */}
        <Link
          to={`/dashboard/property/${data.property_id}`}
          className="relative w-[88px] sm:w-[96px] aspect-square rounded-lg overflow-hidden bg-[#3A3A36] border border-white/10 hover:border-[#C8A66B] transition-colors group shrink-0"
          data-testid="featured-weekly-cover-link"
          aria-label={`Apri ${data.title}`}
        >
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={data.title}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/30">
              <ImageSquare size={28} weight="duotone" />
            </div>
          )}
          {/* Crown badge */}
          <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-[#C8A66B] flex items-center justify-center shadow-md">
            <Crown size={10} weight="fill" className="text-[#1C1C1A]" />
          </div>
        </Link>

        {/* Content */}
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.20em] font-bold text-[#C8A66B]"
              data-testid="featured-weekly-eyebrow"
            >
              <Crown size={10} weight="fill" /> Annuncio della settimana
            </span>
            {/* iter v32 r37p — multi-annuncio: prev/next + counter */}
            {total > 1 && (
              <div className="flex items-center gap-1 text-[10px] text-white/55" data-testid="featured-weekly-pager">
                <button
                  type="button"
                  onClick={goPrev}
                  data-testid="featured-weekly-prev"
                  className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label="Annuncio precedente"
                >
                  <CaretLeft size={11} weight="bold" />
                </button>
                <span className="tabular-nums">
                  {index + 1}/{total}
                </span>
                <button
                  type="button"
                  onClick={goNext}
                  data-testid="featured-weekly-next"
                  className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                  aria-label="Annuncio successivo"
                >
                  <CaretRight size={11} weight="bold" />
                </button>
              </div>
            )}
          </div>
          <h2
            className="text-[15.5px] sm:text-[16.5px] font-semibold tracking-tight leading-tight"
            data-testid="featured-weekly-title"
          >
            Spingi questa casa stasera.
          </h2>
          <p
            className="mt-1 text-[12px] sm:text-[12.5px] text-white/70 leading-snug truncate"
            data-testid="featured-weekly-subtitle"
          >
            <span className="text-white/95 font-medium">{data.title}</span>
            {data.price_str && data.price_str !== "Prezzo su richiesta" && (
              <> · <span className="text-white/95">{data.price_str}</span></>
            )}
          </p>
          <div className="mt-1 flex items-center gap-1 text-[10.5px] text-white/55" data-testid="featured-weekly-reason">
            <Eye size={11} weight="duotone" /> {data.reason}
          </div>

          {/* CTAs */}
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <button
              onClick={downloadImage}
              disabled={downloading}
              data-testid="featured-weekly-download"
              className="inline-flex items-center gap-1 bg-white hover:bg-[#F1F4ED] text-[#1C1C1A] text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              <Download size={12} weight="bold" />
              {downloading ? "Genero…" : "Immagine"}
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="featured-weekly-whatsapp"
              className="inline-flex items-center gap-1 bg-[#25D366] hover:bg-[#1FA855] text-white text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md transition-colors"
            >
              <WhatsappLogo size={12} weight="fill" />
              WhatsApp
            </a>
            <Link
              to={`/dashboard/property/${data.property_id}`}
              data-testid="featured-weekly-open-property"
              className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white px-1.5 py-1.5"
            >
              Apri <ArrowRight size={10} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
